import { mapActions, mapState } from "pinia";
import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";
import useBrandingStore from "%/stores/branding-store";

const CanvasStyleMixinStaging = {
  computed: {
    ...mapState(useDynamicComponentBuilderStore, [
      "elementsToFocus",
      "parentsToFocus",
      "childrenToFocus",
      "currentlyInspectedElementIdentifiers",
      "isDraggingOnBuilder",
      "indicatorIsInLayoutWithIdentifier",
    ]),
    hasABorder() {
      return this.primaryHoverBorder || this.secondaryHoverBorder || this.selectedHoverBorder;
    },
    mouseOverElement() {
      return this.mouseOver || this.mouseOverActions;
    },
    isElementToFocus() {
      return this.elementsToFocus.includes(this.identifier);
    },
    isElementToFocusAndMouseIsOverElement() {
      return this.isElementToFocus && this.mouseOverElement;
    },
    primaryHoverBorder() {
      if (this.isElementToFocus) {
        return true;
      } else if (this.indicatorIsInLayoutWithIdentifier === this.identifier) {
        return true;
      } else if (!this.isDraggingOnBuilder) {
        return this.isElementToFocusAndMouseIsOverElement;
      }
      return false;
    },
    isParentToFocus() {
      return this.parentsToFocus.includes(this.identifier) && this.isLayout;
    },
    isChildToFocus() {
      return this.childrenToFocus.includes(this.identifier);
    },
    secondaryHoverBorder() {
      return this.isParentToFocus || this.isChildToFocus;
    },
    selectedHoverBorder() {
      return this.currentlyInspectedElementIdentifiers.includes(this.identifier);
    },
    isDraggingOverLayout() {
      return this.isDraggingOver && this.isLayout;
    },
    hasOverride() {
      if (useBrandingStore().currentlyInspectedBrandingElementVariant) {
        return false;
      }
      const element = this.dynamicComponentInstance.getElementDefinition(this.identifier);
      if (!element || Object.keys(element).length === 0) {
        return false;
      }
      return this.dynamicComponentInstance.getAllOverwrittenOptionKeys(this.identifier, "default", "default").length;
    },
    showPrimaryHoverBorder() {
      return this.editMode && this.primaryHoverBorder;
    },
    showSecondaryHoverBorder() {
      return this.editMode && this.secondaryHoverBorder && !this.primaryHoverBorder && !this.selectedHoverBorder;
    },
    showSelectedHoverBorder() {
      return this.editMode && this.selectedHoverBorder && !this.primaryHoverBorder;
    },
    showOverriddenBorder() {
      return this.editMode && this.hasOverride && (this.primaryHoverBorder || this.secondaryHoverBorder || this.selectedHoverBorder);
    },
    classList() {
      return {
        "i-dnd": true,
        "drag-over-layout": this.editMode && this.isDraggingOverLayout && this.allowsDrop,
        "dragging-over": this.editMode && this.isDraggingOver,
        "i-dynamic-component": true,
      };
    },
  },
  methods: {
    ...mapActions(useDynamicComponentBuilderStore, [
      "allowsDrop",
      "getElement",
    ]),
    clearHoverBorders() {
      useDynamicComponentBuilderStore().clearElementBorder(this.identifier);
    },
    addHoverBorder() {
      let type;
      if (this.showPrimaryHoverBorder) {
        type = "primary";
      } else if (this.showSecondaryHoverBorder) {
        type = "secondary";
      } else if (this.showSelectedHoverBorder) {
        type = "selected";
      } else {
        return;
      }

      if (this.showOverriddenBorder) {
        type += "-overwritten";
      }

      useDynamicComponentBuilderStore().toggleElementBorder(this.identifier, type, {
        element: this.$el,
      });
    },
  },
  watch: {
    showPrimaryHoverBorder() {
      this.clearHoverBorders();
      this.addHoverBorder();
    },
    showSecondaryHoverBorder() {
      this.clearHoverBorders();
      this.addHoverBorder();
    },
    showSelectedHoverBorder() {
      this.clearHoverBorders();
      this.addHoverBorder();
    },
  },
};

export { CanvasStyleMixinStaging };
